import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button } from "../../components/ui/button";
import CommonContext from "../../hooks/commonContext";
import {
  deleteChannel,
  getChannel,
  getChannelMessage,
  updateChannel,
} from "../../services/apiservices/messages";
import {
  ArrowLeft,
  Globe,
  Lock,
  MessageSquare,
  Trash2,
  Users,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import confirm from "../../components/DialogComponent/confirm";
import { Separator } from "../../components/ui/seprator";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textArea";
import { getFormatedDate } from "../../services/helpers";
import { CommonConfirmationDialog } from "../../components/DialogComponent/SupplyDataDialog";
import { getAllEmployee } from "../../services/apiservices/employee";
import AuthContext from "../../hooks/authContext";
import MultiSelectWithCheckbox from "../../components/SelectField/MulitpleSelectNew";
import SingleSelectNew from "../../components/SelectField/SingleSelectNew";
import { useToast } from "../../hooks/useToast";
import { trackEvent, CMS_EVENTS } from "../../services/apiservices/analytics";

const ManageChannelDetails = () => {
  const navigate = useNavigate();
  const { channelId } = useParams();
  const commonContext = useContext(CommonContext);
  const [allChannels, setAllChannels] = React.useState([]);
  const [messages, setMessages] = React.useState([]);
  const [audienceList, setAudienceList] = React.useState([]);
  const [employeeList, setEmployeeList] = React.useState([]);
  const [formerror, setFormError] = useState({});
  const [channelData, setChannelData] = useState({
    groupName: "",
    description: "",
    type: "",
  });
  const [dialogConfirmOpen, setDialogConfirmOpen] = React.useState(false);
  const [openData, setOpenData] = React.useState(null);
  const auth = useContext(AuthContext);
  const { toast } = useToast();

  const companyId =
    auth?.userProfile?.role === "companyUser"
      ? auth?.userProfile?.id
      : auth?.userProfile?.companyId;

  const setIsLoading = (status) => {
    commonContext.setLoader(status);
  };

  const typeOptions = [
    { label: "Private", value: "Private" },
    { label: "Public", value: "Public" },
  ];

  const fetchChannelDetails = async () => {
    if (!channelId) return;

    commonContext?.setLoader(true);

    const getChannelMessagePromise = new Promise((resolve, reject) => {
      getChannelMessage(channelId, resolve, reject);
    });

    const getChannelPromise = new Promise((resolve, reject) => {
      getChannel(channelId, resolve, reject);
    });

    const getAllEmployeePromise = new Promise((resolve, reject) => {
      getAllEmployee(resolve, reject);
    });

    try {
      const [messages, channelInfo, employeeRes] = await Promise.all([
        getChannelMessagePromise,
        getChannelPromise,
        getAllEmployeePromise,
      ]);

      const filteredEmployees = employeeRes?.data?.filter(
        (item) =>
          ((Array.isArray(item.role) && item.role.includes("Employee")) || item.role === "Employee") &&
          item.status !== false &&
          item.companyId === companyId
      );
      const allEmp = filteredEmployees?.map((eitem) => ({
        ...eitem,
        userName: `${eitem?.firstName} ${eitem?.lastName}`,
      }));
      setEmployeeList(allEmp);

      setAllChannels(channelInfo?.data);
      const audList = [];
      const selectedEmp = channelInfo?.data?.employee?.map((itm) => {
        audList.push(itm);
        return itm?.id;
      });
      setAudienceList(audList);
      setChannelData({
        groupName: channelInfo?.data?.groupName || "",
        description: channelInfo?.data?.description || "",
        type: channelInfo?.data?.type || "",
        selectedEmployees: selectedEmp || [],
      });

      setMessages(messages);
    } catch (error) {
      console.error("Error in fetchChannelDetails:", error);
      if (error?.error === "Channel not found") {
        navigate("/communications/message-board/manage-channel");
      }
    } finally {
      commonContext?.setLoader(false);
    }
  };

  useEffect(() => {
    fetchChannelDetails();
  }, [channelId]);

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case "Public":
        return <Globe className="h-4 w-4" />;
      case "Private":
        return <Lock className="h-4 w-4" />;
      default:
        return <Users className="h-4 w-4" />;
    }
  };

  const getVisibilityColor = (visibility) => {
    switch (visibility) {
      case "Public":
        return "bg-green-100 text-green-800 border-green-200";
      case "Private":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-blue-100 text-blue-800 border-blue-200";
    }
  };

  const handleDialogClose = async () => {
    setDialogConfirmOpen(false);
    setOpenData(null);
  };

  const handleDialogOpen = async (chItem) => {
    setDialogConfirmOpen(true);
    setOpenData(chItem);
  };

  const deleteChannelHandler = async (chItem) => {
    commonContext?.setLoader(true);
    deleteChannel(
      chItem["id"],
      (res) => {
        setDialogConfirmOpen(false);
        setOpenData(null);
        commonContext?.setLoader(false);
        navigate("/communications/message-board/manage-channel")
        fetchChannelDetails();
        window?.location?.reload();
        toast?.({ title: "Success", description: "Channel Deleted Successfully", });
        trackEvent(CMS_EVENTS.CHANNEL_DELETED, {
          channel_id: chItem["id"],
          channel_name: chItem["groupName"],
        });
      },
      (resError) => {
        commonContext?.setLoader(false);
        setDialogConfirmOpen(false);
        setOpenData(null);
        fetchChannelDetails();
      }
    );
  };

  const handlefieldChange = (name, value) => {
    setChannelData({ ...channelData, [name]: value });
    if (name === "selectedEmployees" && value) {
      const empList = employeeList?.filter((emp) => value?.includes(emp?.id));
      setAudienceList([
        ...empList?.map((aitem) => {
          return {
            firstName: aitem.firstName,
            lastName: aitem.lastName,
            id: aitem.id,
            email: aitem.email,
            phone: aitem.phoneNumber,
          };
        }),
      ]);
    }
  };

  const checkValid = () => {
    let errormessages = formerror;
    let isValid = true;
    if (
      typeof channelData?.groupName === "undefined" ||
      channelData?.groupName === ""
    ) {
      isValid = false;
      errormessages = {
        ...errormessages,
        groupName: "Field is required",
      };
    } else {
      errormessages = { ...errormessages, groupName: "" };
    }
    if (channelData?.type === "Private") {
      if (audienceList?.length <= 0) {
        isValid = false;
        errormessages = {
          ...errormessages,
          selectedEmployees:
            "Please Add employees By Name/By Client/By Group First!",
        };
      } else {
        errormessages = { ...errormessages, selectedEmployees: "" };
      }
    } else {
      errormessages = { ...errormessages, selectedEmployees: "" };
    }
    if (
      typeof channelData?.description === "undefined" ||
      channelData?.description === ""
    ) {
      isValid = false;
      errormessages = {
        ...errormessages,
        message: "Field is required",
      };
    } else {
      errormessages = { ...errormessages, message: "" };
    }

    setFormError(errormessages);
    return isValid;
  };

  const onSaveChannel = () => {
    if (true === checkValid()) {
      setIsLoading(true);
      let formDataSave = {
        groupName: channelData?.groupName,
        description: channelData?.description,
        type: channelData?.type,
        companyId: companyId,
        status: true,
        employee: audienceList
          ? audienceList?.map((empitem) => {
            return {
              id: empitem.id,
              firstName: empitem.firstName,
              lastName: empitem.lastName,
              email: empitem.email,
              phone: empitem.phone,
            };
          })
          : [],
      };

      updateChannel(
        channelId,
        formDataSave,
        (res) => {
          if (res.status === true) {
            setIsLoading(false);
            navigate("/communications/message-board/manage-channel");
            toast?.({ title: "Success", description: "Channel Updated Successfully", });
            trackEvent(CMS_EVENTS.CHANNEL_UPDATED, {
              channel_id: channelId,
              channel_name: formDataSave.groupName,
            });
          }
        },
        (errorRes) => {
          setIsLoading(false);
          console.log("error while save notification", errorRes);
        }
      );
    }
  };

  let allempoptions = employeeList?.map((item) => {
    return { label: item?.firstName + item?.lastName, value: item?.id };
  });

  return (
    <div className="min-h-screen dark:bg-[#1A1F2C]">
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:bg-none">
        <div className="mx-auto px-3 sm:px-4 lg:px-8 py-4 lg:py-6 max-w-4xl dark:bg-[#1A1F2C]">
          {/* Header Section */}
          <div className="mb-16 sm:mb-24">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/communications/message-board/manage-channel")}
                className="self-start sm:self-auto dark:bg-[#1A1F2C] dark:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-8 dark:text-white" />
                Back to Channels
              </Button>
              <div className="flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                  Channel Details
                </h1>
                <p className="text-xs sm:text-sm text-muted-foreground mt-4">
                  View and manage channel information
                </p>
              </div>
            </div>
          </div>

          <Card className="border-0 shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] mb-16 sm:mb-24">
            <CardHeader className="pb-12 px-3 sm:px-6 pt-12">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-3">
                  <div className="p-8 bg-primary/10 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-primary" />
                  </div>
                  <span className="break-all dark:text-white">{allChannels?.groupName}</span>
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge
                    style={{ borderRadius: "6px" }}
                    variant="outline"
                    className={`${getVisibilityColor(allChannels?.type)} text-xs`}
                  >
                    {getVisibilityIcon(allChannels?.type)}
                    <span className="ml-4">{allChannels?.type}</span>
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDialogOpen(allChannels)}
                    className="h-8"
                  >
                    <Trash2 className="h-4 w-4 mr-4" />
                    Delete
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              {/* Stats Row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-16 sm:mb-24">
                <div
                  className="text-center p-12 bg-primary/5 rounded-lg bg-[#157F3C1A] shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]"
                >
                  <div
                    className="text-lg sm:text-xl font-bold text-primary"
                    style={{ color: "green" }}
                  >
                    {messages?.length}
                  </div>
                  <div className="text-xs text-muted-foreground">Messages</div>
                </div>
                <div className="text-center p-12 bg-green-50 rounded-lg shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
                  <div className="text-lg sm:text-xl font-bold text-green-600">
                    {allChannels?.employee?.length}
                  </div>
                  <div className="text-xs text-muted-foreground">Members</div>
                </div>
                <div className="text-center p-12 bg-blue-50 rounded-lg col-span-2 sm:col-span-1 shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
                  <div className="text-sm font-bold text-blue-600">
                    {getFormatedDate(allChannels?.updatedAt)}
                  </div>
                  <div className="text-xs text-muted-foreground">Last Active</div>
                </div>
                <div className="text-center p-12 bg-gray-50 rounded-lg col-span-2 sm:col-span-1 shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
                  <div className="text-sm font-bold text-gray-600 dark:text-blue-600">
                    {getFormatedDate(allChannels?.createdOn)}
                  </div>
                  <div className="text-xs text-muted-foreground">Created</div>
                </div>
              </div>

              <Separator className="my-4 sm:my-6" />

              {/* Form Section */}
              <div className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="channelName" className="text-sm font-medium">
                      Channel Name
                    </Label>
                    <Input
                      id="groupName"
                      value={channelData?.groupName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setChannelData((prev) => ({ ...prev, groupName: value }));
                      }}
                      className={`text-sm`}
                    />
                    {typeof formerror?.groupName !== "undefined" &&
                      formerror?.groupName !== "" && (
                        <span className="small error color-danger">
                          {formerror?.groupName}
                        </span>
                      )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="channelType" className="text-sm font-medium">
                      Channel Type
                    </Label>
                    <SingleSelectNew
                      className={`field w-100`}
                      placeholder={"Select Type"}
                      contentClass="bg-white text-black border-gray-300 dark:bg-[#2c3440] dark:text-white dark:border-[#464c59]"
                      itemClass="hover:bg-gray-100 dark:hover:bg-[#3c4351]"
                      value={channelData?.type}
                      onChange={(val) => {
                        setChannelData((prev) => ({ ...prev, type: val }))
                        if (val === "Public") {
                          setAudienceList([]);
                        }
                      }}
                      options={typeOptions}
                    />
                  </div>
                </div>

                {channelData?.type === "Private" && (
                  <div className="space-y-2">
                    <Label htmlFor="members" className="text-sm font-medium">
                      Channel Members
                    </Label>
                    <div className="flex flex-wrap gap-2 p-12 border rounded-md bg-gray-50 min-h-[2.5rem] dark:bg-[#1A1F2C]">
                      <MultiSelectWithCheckbox
                        label="Find Employees by Name"
                        value={
                          channelData?.selectedEmployees
                            ? [...channelData?.selectedEmployees]
                            : []
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth={true}
                        className={`field`}
                        name={"selectedEmployees"}
                        onChange={(val) =>
                          handlefieldChange("selectedEmployees", val)
                        }
                        options={[...allempoptions]}
                      />
                      {typeof formerror?.selectedEmployees !== "undefined" &&
                        formerror?.selectedEmployees !== "" && (
                          <span className="small error color-danger">
                            {formerror?.selectedEmployees}
                          </span>
                        )}
                    </div>
                  </div>
                )}

                <div className="space-y-2 pb-12">
                  <Label htmlFor="description" className="text-sm font-medium">
                    Description
                  </Label>
                  <Textarea
                    id="description"
                    value={channelData?.description}
                    onChange={(e) => {
                      const value = e.target.value;
                      setChannelData((prev) => ({ ...prev, description: value }));
                    }}
                    className={`min-h-[100px] text-sm resize-none`}
                    placeholder="Enter channel description..."
                  />
                  {typeof formerror?.message !== "undefined" &&
                    formerror?.message !== "" && (
                      <span className="small error color-danger">
                        {formerror?.message}
                      </span>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-12 pb-12">
                  <Button
                    onClick={onSaveChannel}
                    className="bg-green-600 hover:bg-green-700 text-white flex-1 sm:flex-none"
                  >
                    Save
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        const result = await confirm("Are you sure you want to cancel? This won't be saved or sent.");
                        if (result) {
                          navigate("/communications/message-board/manage-channel");
                        }
                      } catch (err) {
                        console.log("Dialog closed or error:", err);
                      }
                    }}
                    variant="outline"
                    className="flex-1 sm:flex-none"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <CommonConfirmationDialog
            open={dialogConfirmOpen}
            onConfirm={() => deleteChannelHandler(openData)}
            onClose={handleDialogClose}
            message={`Are you Sure you want to permanent remove ${openData ? openData['groupName'] : ""} channel?`}
            title={"Remove Channel"}
            buttonTitle="Yes"
          />
        </div>
      </div>
    </div>
  );
};

export default ManageChannelDetails;
